import type { MouseEvent } from 'react';
import type { Nullable } from 'corgi-types';

import type { Translate } from 'common/providers/LocaleProvider';
import { useLocalization } from 'common/providers/LocaleProvider';
import type { DispatchState } from 'common/types/React';
import type { WithArticleProps } from 'common/types/reclamation';
import {
  isReclamationRequestGraphQLError,
  RECLAMATION_REQUEST_ERROR_CODE,
} from 'common/utils/reclamation/error';
import type { DrawerAlert } from '../hooks/alert';
import type { ReclamationDrawerAction, ReclamationDrawerOpenAction, Step } from '../types/drawer';
import type { StepSubmitHandler } from '../types/stepSubmitHandler';
import { getRemainingSteps } from '../utils/drawer';
import { ReclamationError } from '../utils/ReclamationError';
import { ValidationError } from '../utils/ValidationError';

export interface UseFormSubmitHandlerParams {
  state: Nullable<ReclamationDrawerAction>;
  steps: Array<WithArticleProps<Step>>;
  activeStep: WithArticleProps<Step>;
  setSteps: (steps: Array<WithArticleProps<Step>>) => void;
  setAlert: DispatchState<DrawerAlert>;
  stepSubmitHandler: StepSubmitHandler;
  setIsNextDisabled: DispatchState<boolean>;
  navigateToNextStep: (currentSteps?: Array<WithArticleProps<Step>>) => boolean;
  setHasInvalidField: DispatchState<boolean>;
}

const isReclamationError = (error: unknown): error is ReclamationError =>
  error instanceof ReclamationError;

const isValidationError = (error: unknown): error is ValidationError =>
  error instanceof ValidationError;

const isReclamationAlreadySubmittedErrorCode = (errorCode: RECLAMATION_REQUEST_ERROR_CODE) =>
  errorCode === RECLAMATION_REQUEST_ERROR_CODE.RECLAMATION_ALREADY_SUBMITTED;

const getDrawerAlertMsg = (err: unknown, translate: Translate) =>
  isReclamationError(err)
    ? { message: err.message, type: err.type }
    : isReclamationRequestGraphQLError(err) &&
        isReclamationAlreadySubmittedErrorCode(err.extensions.code)
      ? { message: translate('h24_reclamation_error_already_submitted') }
      : { message: translate('h24_something_went_wrong') };

export const useFormSubmitHandler = ({
  state,
  steps,
  activeStep,
  setSteps,
  setAlert,
  stepSubmitHandler,
  setIsNextDisabled,
  navigateToNextStep,
  setHasInvalidField,
}: UseFormSubmitHandlerParams) => {
  const { translate } = useLocalization();

  const submitHandler = async (event: MouseEvent) => {
    event.preventDefault();
    setAlert(null);

    try {
      const response = await stepSubmitHandler();

      const { onFinish } = state as ReclamationDrawerOpenAction;
      let currentSteps: Array<WithArticleProps<Step>> | undefined;

      setIsNextDisabled(true);

      if (response && response.data) {
        const [isChangeNeeded, newSteps] = getRemainingSteps({
          activeStep,
          steps,
          options: response.data,
        });

        if (isChangeNeeded) {
          setSteps(newSteps);
          currentSteps = newSteps;
        }
      }

      if (navigateToNextStep(currentSteps)) {
        onFinish();
      }
    } catch (err) {
      if (isValidationError(err)) {
        setHasInvalidField(true);
      } else {
        setAlert(getDrawerAlertMsg(err, translate));
        setIsNextDisabled(false);
      }
    }
  };

  return submitHandler;
};
